import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tenantService } from '../services/api';
import { CreateTenantModal, EditTenantModal, ArchiveConfirmModal } from '../components/TenantModals';
import { ProductChip } from '../components/TenantComponents';

const TenantList = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: 'active', type: 'all', product: 'all' });
  const [sorting, setSorting] = useState({ sortBy: 'name', sortOrder: 'asc' });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [archiveTenant, setArchiveTenant] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const includeArchived = filters.status === 'all' || filters.status === 'archived';
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        includeArchived: includeArchived ? 'true' : 'false',
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      };
      if (filters.type !== 'all') {
        params.type = filters.type;
      }
      // Enrollment filter is server-side (WHERE EXISTS on tenant_products) —
      // unlike the 'archived only' filter below, it must not be applied to the
      // fetched page or the pagination totals would lie.
      if (filters.product !== 'all') {
        params.product = filters.product;
      }
      const response = await tenantService.getAll(params);
      
      let data = response.data || [];
      // Client-side filter for 'archived only' since backend returns both when includeArchived=true
      if (filters.status === 'archived') {
        data = data.filter(t => !t.isActive);
      }
      
      setTenants(data);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.status, filters.type, filters.product, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleStatusFilter = (e) => {
    setFilters({ ...filters, status: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return '↕';
    return sorting.sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleArchive = async (tenant) => {
    setActionLoading(tenant.id);
    try {
      await tenantService.delete(tenant.id);
      setMessage(`Tenant "${tenant.name}" archived successfully`);
      setArchiveTenant(null);
      fetchTenants();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (tenant) => {
    setActionLoading(tenant.id);
    try {
      await tenantService.restore(tenant.id);
      setMessage(`Tenant "${tenant.name}" restored successfully`);
      fetchTenants();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span className="status-badge status-active">Active</span>;
    }
    return <span className="status-badge status-disabled">Archived</span>;
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Tenants</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Add Tenant
        </button>
      </div>

      {message && (
        <div className="alert alert-success">{message}</div>
      )}
      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search tenants by name..."
            value={filters.search}
            onChange={handleSearch}
          />
        </div>
        <select value={filters.status} onChange={handleStatusFilter}>
          <option value="active">Active Only</option>
          <option value="archived">Archived Only</option>
          <option value="all">All Tenants</option>
        </select>
        <select value={filters.type} onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setPagination({ ...pagination, page: 1 }); }}>
          <option value="all">All Types</option>
          <option value="organization">Organization</option>
          <option value="personal">Personal</option>
        </select>
        <select value={filters.product} onChange={(e) => { setFilters({ ...filters, product: e.target.value }); setPagination({ ...pagination, page: 1 }); }}>
          <option value="all">All Products</option>
          <option value="brands_manager">Brands Manager</option>
          <option value="media_console">Media Console</option>
          <option value="gridrock">GridRock</option>
        </select>
      </div>

      {/* Tenant Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    Tenant {getSortIcon('name')}
                  </th>
                  <th>Type</th>
                  <th>Products</th>
                  <th>Status</th>
                  <th className="sortable" onClick={() => handleSort('user_count')}>
                    Users {getSortIcon('user_count')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('campaign_count')}>
                    Campaigns {getSortIcon('campaign_count')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('device_count')}>
                    Devices {getSortIcon('device_count')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>
                    Created {getSortIcon('created_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-state">
                      No tenants found
                    </td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td className="user-cell">
                        <div className="user-avatar" aria-label={`Avatar for ${tenant.name}`}>
                          {tenant.name?.charAt(0) || '?'}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            <Link to={`/tenants/${tenant.id}`}>{tenant.name}</Link>
                          </div>
                          <div className="user-email">
                            {tenant.ownerEmail ? `Owner: ${tenant.ownerName || tenant.ownerEmail}` : (tenant.description ? 
                              (tenant.description.length > 40 ? tenant.description.substring(0, 40) + '...' : tenant.description) 
                              : 'No description')}
                          </div>
                        </div>
                      </td>
                      <td>
                        {tenant.isPersonal ? (
                          <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0' }}>Personal</span>
                        ) : (
                          <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#f3e5f5', color: '#7b1fa2' }}>Organization</span>
                        )}
                      </td>
                      <td>
                        {(tenant.products || []).length ? (
                          tenant.products.map(p => <ProductChip key={p.product} product={p.product} />)
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </td>
                      <td>{getStatusBadge(tenant.isActive)}</td>
                      <td>
                        <Link to={`/users?tenantId=${tenant.id}`} title="View users in this tenant">
                          {tenant.userCount || 0}
                        </Link>
                      </td>
                      <td>{tenant.campaignCount || 0}</td>
                      <td>
                        <Link to={`/devices?tenantId=${tenant.id}`} title="View devices assigned to this tenant">
                          {tenant.deviceCount || 0}
                        </Link>
                      </td>
                      <td>{formatDate(tenant.createdAt)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === tenant.id ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            <Link to={`/tenants/${tenant.id}`} className="dropdown-item">
                              View Details
                            </Link>
                            <button className="dropdown-item" onClick={() => setEditTenant(tenant)}>
                              Edit Tenant
                            </button>
                            {tenant.isActive ? (
                              <button 
                                className="dropdown-item text-danger" 
                                onClick={() => setArchiveTenant(tenant)}
                              >
                                Archive
                              </button>
                            ) : (
                              <button 
                                className="dropdown-item text-success" 
                                onClick={() => handleRestore(tenant)}
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && pagination.totalPages > 1 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} tenants</span>
              <div className="pagination-buttons">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button 
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowCreateModal(false); fetchTenants(); }}
          onError={setError}
        />
      )}

      {/* Edit Tenant Modal */}
      {editTenant && (
        <EditTenantModal
          tenant={editTenant}
          onClose={() => setEditTenant(null)}
          onSuccess={(msg) => { setMessage(msg); setEditTenant(null); fetchTenants(); }}
          onError={setError}
        />
      )}

      {/* Archive Confirmation Modal */}
      {archiveTenant && (
        <ArchiveConfirmModal
          tenant={archiveTenant}
          onClose={() => setArchiveTenant(null)}
          onConfirm={() => handleArchive(archiveTenant)}
          loading={actionLoading === archiveTenant.id}
        />
      )}
    </div>
  );
};

export default TenantList;
